import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { mockFormSubmit } from '../mock';
import { toast } from 'sonner';

const countryCodes = [
  { code: '+1', country: 'USA/Canada' },
  { code: '+44', country: 'UK' },
  { code: '+91', country: 'India' },
  { code: '+61', country: 'Australia' },
  { code: '+86', country: 'China' }
];

export const ContactPopup = ({ isOpen, onClose, defaultMessage = '' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    phone: '',
    message: defaultMessage
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.phone.length > 10) {
      toast.error('Phone number cannot exceed 10 digits');
      return;
    }

    if (!/^\d+$/.test(formData.phone)) {
      toast.error('Phone number must contain only digits');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await mockFormSubmit({
        ...formData,
        fullPhone: `${formData.countryCode} ${formData.phone}`
      });
      
      if (result.success) {
        toast.success(result.message);
        setFormData({
          name: '',
          email: '',
          countryCode: '+1',
          phone: '',
          message: ''
        });
        onClose();
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] bg-[#EFEBE9]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#3E2723]">
            Get Started Today
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label htmlFor="name" className="text-[#3E2723]">Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Your full name"
              className="mt-1 border-[#8D6E63] focus:border-[#3E2723]"
              required
            />
          </div>

          <div>
            <Label htmlFor="email" className="text-[#3E2723]">Email *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="your@email.com"
              className="mt-1 border-[#8D6E63] focus:border-[#3E2723]"
              required
            />
          </div>

          <div>
            <Label htmlFor="phone" className="text-[#3E2723]">Contact Number *</Label>
            <div className="flex gap-2 mt-1">
              <Select 
                value={formData.countryCode} 
                onValueChange={(value) => setFormData({ ...formData, countryCode: value })}
              >
                <SelectTrigger className="w-[140px] border-[#8D6E63]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {countryCodes.map((item) => (
                    <SelectItem key={item.code} value={item.code}>
                      {item.code} {item.country}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setFormData({ ...formData, phone: value });
                }}
                placeholder="1234567890"
                maxLength={10}
                className="flex-1 border-[#8D6E63] focus:border-[#3E2723]"
                required
              />
            </div>
          </div>

          <div>
            <Label htmlFor="message" className="text-[#3E2723]">Message</Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder="Tell us about your trademark needs..."
              className="mt-1 border-[#8D6E63] focus:border-[#3E2723] min-h-[100px]"
              rows={4}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 border-[#8D6E63] text-[#3E2723] hover:bg-[#D7CCC8]"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-[#3E2723] hover:bg-[#5D4037] text-white"
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
